import moment from "moment";
import { Reservation } from "src/types";

/**
 * Finds existing reservations that conflict with a new reservation
 * @param existingReservations - Array of existing reservations to check against
 * @param newReservation - The new reservation to check for conflicts
 * @returns Array of reservation IDs that conflict with the new reservation
 */
export const findReservationConflicts = (
  existingReservations: Reservation[],
  newReservation: Reservation
): number[] => {
  const conflictingIds: number[] = [];

  // Handle non-recurring new reservations
  if (!newReservation.recurring_frequency) {
    // Parse the new reservation's date and time
    const newStartDateTime = moment(`${newReservation.start_date} ${newReservation.start_time}`);
    const newEndDateTime = moment(`${newReservation.end_date || newReservation.start_date} ${newReservation.end_time}`);

    existingReservations.forEach(existingReservation => {
      // Skip if it's the same reservation (when editing)
      if (existingReservation.id === newReservation.id) {
        return;
      }

      if (!existingReservation.recurring_frequency) {
        // Both are non-recurring - simple date/time overlap check
        const existingStartDateTime = moment(`${existingReservation.start_date} ${existingReservation.start_time}`);
        const existingEndDateTime = moment(`${existingReservation.end_date || existingReservation.start_date} ${existingReservation.end_time}`);

        // Check for time overlap: two events overlap if one starts before the other ends
        const isOverlapping = newStartDateTime.isBefore(existingEndDateTime) && newEndDateTime.isAfter(existingStartDateTime);
        
        if (isOverlapping && existingReservation.id) {
          conflictingIds.push(existingReservation.id);
        }
      } else {
        // Existing reservation is recurring - need to check if any of its occurrences conflict
        const conflictingEvents = findRecurringConflicts(existingReservation, newStartDateTime, newEndDateTime);
        
        if (conflictingEvents.length > 0 && existingReservation.id) {
          conflictingIds.push(existingReservation.id);
        }
      }
    });

    return conflictingIds;
  } else {
    // Handle recurring new reservations
    existingReservations.forEach(existingReservation => {
      // Skip if it's the same reservation (when editing)
      if (existingReservation.id === newReservation.id) {
        return;
      }

      if (!existingReservation.recurring_frequency) {
        // Existing reservation is non-recurring, new reservation is recurring
        // Use findRecurringConflicts with swapped parameters
        const existingStartDateTime = moment(`${existingReservation.start_date} ${existingReservation.start_time}`);
        const existingEndDateTime = moment(`${existingReservation.end_date || existingReservation.start_date} ${existingReservation.end_time}`);
        
        const conflictingEvents = findRecurringConflicts(newReservation, existingStartDateTime, existingEndDateTime);
        
        if (conflictingEvents.length > 0 && existingReservation.id) {
          conflictingIds.push(existingReservation.id);
        }
      } else {
        // Both reservations are recurring - this is the most complex case
        // We need to check if any occurrence of the new reservation conflicts with any occurrence of the existing reservation
        // Here be dragons!
        const conflicts = findRecurringVsRecurringConflicts(newReservation, existingReservation);
        
        if (conflicts && existingReservation.id) {
          conflictingIds.push(existingReservation.id);
        }
      }
    });

    return conflictingIds;
  }
};

/**
 * Helper function to find if a recurring reservation conflicts with a specific time range
 * @param recurringReservation - The recurring reservation to check
 * @param targetStartDateTime - Start time of the target time range
 * @param targetEndDateTime - End time of the target time range
 * @returns Array of conflicting event dates (for debugging/logging purposes)
 */
const findRecurringConflicts = (
  recurringReservation: Reservation,
  targetStartDateTime: moment.Moment,
  targetEndDateTime: moment.Moment
): moment.Moment[] => {
  const conflictingEvents: moment.Moment[] = [];
  const frequency = recurringReservation.recurring_frequency;
  
  if (!frequency) {
    return conflictingEvents;
  }

  // Get the recurring gap, default to 1 if not specified
  const recurringGap = recurringReservation.recurring_gap && recurringReservation.recurring_gap > 0 
    ? Math.floor(recurringReservation.recurring_gap) : 1;

  // Parse original start date and time
  const originalStartDate = moment(`${recurringReservation.start_date} ${recurringReservation.start_time}`);
  
  // Handle end date - if falsy, event goes on forever (but we'll limit our search)
  const hasEndDate = recurringReservation.end_date && recurringReservation.end_time;
  const originalEndDate = hasEndDate 
    ? moment(`${recurringReservation.end_date} ${recurringReservation.end_time}`)
    : null;

  // Calculate event duration for each occurrence
  const eventDuration = moment.duration(
    moment(recurringReservation.end_time, ['HH:mm:ss', 'HH:mm']).diff(
      moment(recurringReservation.start_time, ['HH:mm:ss', 'HH:mm'])
    )
  );

  // Define a reasonable search range around the target time
  // Look back and forward enough to catch relevant recurring events
  const searchStart = moment(targetStartDateTime).subtract(2, 'years');
  const searchEnd = moment(targetEndDateTime).add(2, 'years');
  
  // Limit search end by the recurring reservation's end date if it exists
  const effectiveSearchEnd = hasEndDate && originalEndDate && originalEndDate.isBefore(searchEnd) 
    ? originalEndDate 
    : searchEnd;

  // Handle monthly/yearly with week_number and day_of_week
  if ((frequency === 'monthly' || frequency === 'yearly') && 
      recurringReservation.week_number && recurringReservation.day_of_week && 
      !recurringReservation.recurring_date) {
    
    // Start from the appropriate month
    let currentMonth = frequency === 'monthly'
      ? moment(originalStartDate).startOf('month')
      : moment(originalStartDate).month(recurringReservation.recurring_month ? recurringReservation.recurring_month - 1 : originalStartDate.month()).startOf('month');
    
    // Advance to search range if needed
    if (currentMonth.isBefore(searchStart, 'month')) {
      if (frequency === 'monthly') {
        const monthsDiff = searchStart.diff(currentMonth, 'months');
        const periodsToAdvance = Math.floor(monthsDiff / recurringGap);
        currentMonth.add(periodsToAdvance * recurringGap, 'months');
      } else if (frequency === 'yearly') {
        const targetYear = searchStart.year();
        const targetMonth = recurringReservation.recurring_month ? recurringReservation.recurring_month - 1 : originalStartDate.month();
        currentMonth = moment().year(targetYear).month(targetMonth).startOf('month');
        
        if (currentMonth.isBefore(searchStart, 'month')) {
          currentMonth.add(1, 'year');
        }
      }
    }
    
    // Check occurrences
    while (currentMonth.isSameOrBefore(effectiveSearchEnd, frequency === 'monthly' ? 'month' : 'year')) {
      try {
        if (frequency === 'yearly' && recurringReservation.recurring_month) {
          currentMonth.month(recurringReservation.recurring_month - 1);
        }
        
        const eventDate = findDateInMonth(currentMonth, recurringReservation.week_number, recurringReservation.day_of_week);
        const eventStartDateTime = moment(eventDate).hour(originalStartDate.hour()).minute(originalStartDate.minute());
        const eventEndDateTime = moment(eventStartDateTime).add(eventDuration);
        
        // Check if this occurrence conflicts with target range
        if (eventStartDateTime.isSameOrAfter(originalStartDate) &&
            eventStartDateTime.isBefore(targetEndDateTime) && 
            eventEndDateTime.isAfter(targetStartDateTime)) {
          conflictingEvents.push(eventStartDateTime);
        }
      } catch (error) {
        // Skip months where the target week/day doesn't exist
      }
      
      // Advance to next occurrence
      if (frequency === 'monthly') {
        currentMonth.add(recurringGap, 'months');
      } else if (frequency === 'yearly') {
        currentMonth.add(1, 'year');
      }
    }
    
    return conflictingEvents;
  }

  // Handle monthly/yearly with specific recurring_date
  if ((frequency === 'monthly' || frequency === 'yearly') && recurringReservation.recurring_date) {
    let currentMonth = frequency === 'monthly'
      ? moment(originalStartDate).startOf('month')
      : moment(originalStartDate).month(recurringReservation.recurring_month ? recurringReservation.recurring_month - 1 : originalStartDate.month()).startOf('month');
    
    // Advance to search range if needed
    if (currentMonth.isBefore(searchStart, 'month')) {
      if (frequency === 'monthly') {
        const monthsDiff = searchStart.diff(currentMonth, 'months');
        const periodsToAdvance = Math.floor(monthsDiff / recurringGap);
        currentMonth.add(periodsToAdvance * recurringGap, 'months');
      } else if (frequency === 'yearly') {
        const targetYear = searchStart.year();
        const targetMonth = recurringReservation.recurring_month ? recurringReservation.recurring_month - 1 : originalStartDate.month();
        currentMonth = moment().year(targetYear).month(targetMonth).startOf('month');
        
        if (currentMonth.isBefore(searchStart, 'month')) {
          currentMonth.add(1, 'year');
        }
      }
    }
    
    // Check occurrences
    while (currentMonth.isSameOrBefore(effectiveSearchEnd, frequency === 'monthly' ? 'month' : 'year')) {
      if (frequency === 'yearly' && recurringReservation.recurring_month) {
        currentMonth.month(recurringReservation.recurring_month - 1);
      }
      
      const eventDate = currentMonth.clone().date(recurringReservation.recurring_date);
      
      // Check if the date is valid (e.g., February 30th doesn't exist)
      if (eventDate.date() === recurringReservation.recurring_date) {
        const eventStartDateTime = moment(eventDate).hour(originalStartDate.hour()).minute(originalStartDate.minute());
        const eventEndDateTime = moment(eventStartDateTime).add(eventDuration);
        
        // Check if this occurrence conflicts with target range
        if (eventStartDateTime.isSameOrAfter(originalStartDate) &&
            eventStartDateTime.isBefore(targetEndDateTime) && 
            eventEndDateTime.isAfter(targetStartDateTime)) {
          conflictingEvents.push(eventStartDateTime);
        }
      }
      
      // Advance to next occurrence
      if (frequency === 'monthly') {
        currentMonth.add(recurringGap, 'months');
      } else if (frequency === 'yearly') {
        currentMonth.add(1, 'year');
      }
    }
    
    return conflictingEvents;
  }

  // Handle daily, weekly, and simpler monthly/yearly patterns
  let currentDate = moment(originalStartDate);
  
  // If original date is before the search range, advance to the start of search
  if (currentDate.isBefore(searchStart)) {
    const dayOfMonth = originalStartDate.date();
    const month = originalStartDate.month();

    switch (frequency) {
      case 'daily':
        while (currentDate.isBefore(searchStart)) {
          currentDate.add(recurringGap, 'day');
        }
        break;
        
      case 'weekly':
        while (currentDate.isBefore(searchStart)) {
          currentDate.add(recurringGap, 'week');
        }
        break;

      case 'monthly':
        while (currentDate.isBefore(searchStart)) {
          currentDate.add(recurringGap, 'month');
          const lastDayOfMonth = currentDate.daysInMonth();
          if (dayOfMonth > lastDayOfMonth) {
            currentDate.date(lastDayOfMonth);
          } else {
            currentDate.date(dayOfMonth);
          }
        }
        break;
        
      case 'yearly':
        while (currentDate.isBefore(searchStart)) {
          currentDate.add(1, 'year');
          if (month === 1 && dayOfMonth === 29 && !moment([currentDate.year()]).isLeapYear()) {
            currentDate.month(month).date(28);
          } else {
            currentDate.month(month).date(dayOfMonth);
          }
        }
        break;
    }
  }
  
  // Check occurrences within search range
  while (currentDate.isSameOrBefore(effectiveSearchEnd)) {
    if (frequency === 'weekly' && recurringReservation.days_of_week && recurringReservation.days_of_week.length > 0) {
      // Handle multiple days of the week
      const currentDayOfWeek = currentDate.isoWeekday();
      
      for (const targetDayOfWeek of recurringReservation.days_of_week) {
        let daysToAdd = ((targetDayOfWeek - currentDayOfWeek) + 7) % 7;
        const eventDate = moment(currentDate).add(daysToAdd, 'days');
        
        if (eventDate.isSameOrBefore(effectiveSearchEnd)) {
          const eventStartDateTime = moment(eventDate).hour(originalStartDate.hour()).minute(originalStartDate.minute());
          const eventEndDateTime = moment(eventStartDateTime).add(eventDuration);
          
          // Check if this occurrence conflicts with target range
          if (eventStartDateTime.isBefore(targetEndDateTime) && eventEndDateTime.isAfter(targetStartDateTime)) {
            conflictingEvents.push(eventStartDateTime);
          }
        }
      }
    } else {
      // Handle single day occurrences
      const eventStartDateTime = moment(currentDate).hour(originalStartDate.hour()).minute(originalStartDate.minute());
      const eventEndDateTime = moment(eventStartDateTime).add(eventDuration);
      
      // Check if this occurrence conflicts with target range
      if (eventStartDateTime.isBefore(targetEndDateTime) && eventEndDateTime.isAfter(targetStartDateTime)) {
        conflictingEvents.push(eventStartDateTime);
      }
    }

    // Advance to next occurrence
    const dayOfMonth = originalStartDate.date();
    const lastDayOfMonth = currentDate.daysInMonth();
    switch (frequency) {
      case 'daily':
        currentDate.add(recurringGap, 'day');
        break;
        
      case 'weekly':
        currentDate.add(recurringGap, 'week');
        break;
        
      case 'monthly':
        currentDate.add(recurringGap, 'month');
        if (dayOfMonth > lastDayOfMonth) {
          currentDate.date(lastDayOfMonth);
        } else {
          currentDate.date(dayOfMonth);
        }
        break;
        
      case 'yearly':
        currentDate.add(1, 'year');
        break;
    }
  }
  
  return conflictingEvents;
};

/**
 * Helper function to find a specific date in a month (reused from your existing code)
 */
const findDateInMonth = (
  monthStart: moment.Moment, 
  weekNumber: number, 
  dayOfWeek: number
): moment.Moment => {
  const month = monthStart.clone();
  
  if (weekNumber === -1) {
    // Last occurrence of dayOfWeek in the month
    const lastDayOfMonth = month.clone().endOf('month');
    const lastWeekday = lastDayOfMonth.clone();
    
    // Move backwards to find the last occurrence of the target day
    while (lastWeekday.isoWeekday() !== dayOfWeek) {
      lastWeekday.subtract(1, 'day');
    }
    
    return lastWeekday;
  } else {
    // Nth occurrence of dayOfWeek in the month (1st, 2nd, 3rd, 4th)
    const firstDayOfMonth = month.clone().startOf('month');
    let targetDate = firstDayOfMonth.clone();
    
    // Find the first occurrence of the target day
    while (targetDate.isoWeekday() !== dayOfWeek) {
      targetDate.add(1, 'day');
    }
    
    // Add weeks to get to the Nth occurrence
    targetDate.add((weekNumber - 1) * 7, 'days');
    
    // Check if we're still in the same month
    if (targetDate.month() !== month.month()) {
      throw new Error(`Week ${weekNumber} of day ${dayOfWeek} does not exist in ${month.format('YYYY-MM')}`);
    }
    
    return targetDate;
  }
};

/**
* Helper function to check if two recurring reservations conflict with each other
* This is the most complex case - we need to find if any occurrence of one overlaps with any occurrence of the other
* @param newReservation - The new recurring reservation
* @param existingReservation - The existing recurring reservation
* @returns boolean indicating if there are any conflicts
*/
const findRecurringVsRecurringConflicts = (
 newReservation: Reservation,
 existingReservation: Reservation
): boolean => {
 // Define a reasonable search range (2 years back and forward)
 const searchStart = moment().subtract(2, 'years');
 const searchEnd = moment().add(2, 'years');
 
 // Generate occurrences for the new reservation within the search range
 const newReservationOccurrences = generateReservationOccurrences(newReservation, searchStart, searchEnd);
 
 // For each occurrence of the new reservation, check if it conflicts with the existing recurring reservation
 for (const newOccurrence of newReservationOccurrences) {
   const conflicts = findRecurringConflicts(
     existingReservation, 
     newOccurrence.startDateTime, 
     newOccurrence.endDateTime
   );
   
   if (conflicts.length > 0) {
     return true; // Found at least one conflict
   }
 }
 
 return false; // No conflicts found
};

/**
* Helper function to generate all occurrences of a recurring reservation within a date range
* @param reservation - The recurring reservation
* @param searchStart - Start of the search range
* @param searchEnd - End of the search range
* @returns Array of occurrence objects with start and end times
*/
const generateReservationOccurrences = (
  reservation: Reservation,
  searchStart: moment.Moment,
  searchEnd: moment.Moment
): Array<{ startDateTime: moment.Moment; endDateTime: moment.Moment }> => {
  const occurrences: Array<{ startDateTime: moment.Moment; endDateTime: moment.Moment }> = [];

  const frequency = reservation.recurring_frequency;

  if (!frequency) {
    return occurrences;
  }

  const recurringGap = reservation.recurring_gap && reservation.recurring_gap > 0 
    ? Math.floor(reservation.recurring_gap) : 1;

  const originalStartDate = moment(`${reservation.start_date} ${reservation.start_time}`);

  // Handle end date - cut off at 2 years from now if no end date or end date is too far in the future
  const hasEndDate = reservation.end_date && reservation.end_time;
  const originalEndDate = hasEndDate 
    ? moment(`${reservation.end_date} ${reservation.end_time}`)
    : null;

  // Enforce 2-year cutoff
  const twoYearsFromNow = moment().add(2, 'years');
  const effectiveSearchEnd = hasEndDate && originalEndDate && originalEndDate.isBefore(searchEnd) 
    ? (originalEndDate.isBefore(twoYearsFromNow) ? originalEndDate : twoYearsFromNow)
    : (searchEnd.isBefore(twoYearsFromNow) ? searchEnd : twoYearsFromNow);

  const eventDuration = moment.duration(
    moment(reservation.end_time, ['HH:mm:ss', 'HH:mm']).diff(
      moment(reservation.start_time, ['HH:mm:ss', 'HH:mm'])
    )
  );

  // Handle monthly/yearly with week_number and day_of_week
  if ((frequency === 'monthly' || frequency === 'yearly') && 
      reservation.week_number && reservation.day_of_week && 
      !reservation.recurring_date) {
    
    let currentMonth = frequency === 'monthly'
      ? moment(originalStartDate).startOf('month')
      : moment(originalStartDate).month(reservation.recurring_month ? reservation.recurring_month - 1 : originalStartDate.month()).startOf('month');
    
    // Advance to search range if needed
    if (currentMonth.isBefore(searchStart, 'month')) {
      if (frequency === 'monthly') {
        const monthsDiff = searchStart.diff(currentMonth, 'months');
        const periodsToAdvance = Math.floor(monthsDiff / recurringGap);
        currentMonth.add(periodsToAdvance * recurringGap, 'months');
      } else if (frequency === 'yearly') {
        const targetYear = searchStart.year();
        const targetMonth = reservation.recurring_month ? reservation.recurring_month - 1 : originalStartDate.month();
        currentMonth = moment().year(targetYear).month(targetMonth).startOf('month');
        
        if (currentMonth.isBefore(searchStart, 'month')) {
          currentMonth.add(1, 'year');
        }
      }
    }
    
    while (currentMonth.isSameOrBefore(effectiveSearchEnd, frequency === 'monthly' ? 'month' : 'year')) {
      try {
        if (frequency === 'yearly' && reservation.recurring_month) {
          currentMonth.month(reservation.recurring_month - 1);
        }
        
        const eventDate = findDateInMonth(currentMonth, reservation.week_number, reservation.day_of_week);
        const eventStartDateTime = moment(eventDate).hour(originalStartDate.hour()).minute(originalStartDate.minute());
        const eventEndDateTime = moment(eventStartDateTime).add(eventDuration);
        
        if (eventStartDateTime.isSameOrAfter(originalStartDate) &&
            eventStartDateTime.isSameOrAfter(searchStart) && 
            eventStartDateTime.isSameOrBefore(effectiveSearchEnd)) {
          occurrences.push({
            startDateTime: eventStartDateTime.clone(),
            endDateTime: eventEndDateTime.clone()
          });
        }
      } catch (error) {
        // Skip months where the target week/day doesn't exist
      }
      
      if (frequency === 'monthly') {
        currentMonth.add(recurringGap, 'months');
      } else if (frequency === 'yearly') {
        currentMonth.add(1, 'year');
      }
    }
    
    return occurrences;
  }

  // Handle monthly/yearly with specific recurring_date
  if ((frequency === 'monthly' || frequency === 'yearly') && reservation.recurring_date) {
    let currentMonth = frequency === 'monthly'
      ? moment(originalStartDate).startOf('month')
      : moment(originalStartDate).month(reservation.recurring_month ? reservation.recurring_month - 1 : originalStartDate.month()).startOf('month');
    
    // Advance to search range if needed
    if (currentMonth.isBefore(searchStart, 'month')) {
      if (frequency === 'monthly') {
        const monthsDiff = searchStart.diff(currentMonth, 'months');
        const periodsToAdvance = Math.floor(monthsDiff / recurringGap);
        currentMonth.add(periodsToAdvance * recurringGap, 'months');
      } else if (frequency === 'yearly') {
        const targetYear = searchStart.year();
        const targetMonth = reservation.recurring_month ? reservation.recurring_month - 1 : originalStartDate.month();
        currentMonth = moment().year(targetYear).month(targetMonth).startOf('month');
        
        if (currentMonth.isBefore(searchStart, 'month')) {
          currentMonth.add(1, 'year');
        }
      }
    }
    
    while (currentMonth.isSameOrBefore(effectiveSearchEnd, frequency === 'monthly' ? 'month' : 'year')) {
      if (frequency === 'yearly' && reservation.recurring_month) {
        currentMonth.month(reservation.recurring_month - 1);
      }
      
      const eventDate = currentMonth.clone().date(reservation.recurring_date);
      
      // Check if the date is valid (e.g., February 30th doesn't exist)
      if (eventDate.date() === reservation.recurring_date) {
        const eventStartDateTime = moment(eventDate).hour(originalStartDate.hour()).minute(originalStartDate.minute());
        const eventEndDateTime = moment(eventStartDateTime).add(eventDuration);
        
        if (eventStartDateTime.isSameOrAfter(originalStartDate) &&
            eventStartDateTime.isSameOrAfter(searchStart) && 
            eventStartDateTime.isSameOrBefore(effectiveSearchEnd)) {
          occurrences.push({
            startDateTime: eventStartDateTime.clone(),
            endDateTime: eventEndDateTime.clone()
          });
        }
      }
      
      if (frequency === 'monthly') {
        currentMonth.add(recurringGap, 'months');
      } else if (frequency === 'yearly') {
        currentMonth.add(1, 'year');
      }
    }
    
    return occurrences;
  }

  // Handle daily, weekly, and simpler patterns
  let currentDate = moment(originalStartDate);

  // If original date is before the search range, advance to the start of search
  if (currentDate.isBefore(searchStart)) {
    const dayOfMonth = originalStartDate.date();
    const month = originalStartDate.month();

    switch (frequency) {
      case 'daily':
        while (currentDate.isBefore(searchStart)) {
          currentDate.add(recurringGap, 'day');
        }
        break;
        
      case 'weekly':
        while (currentDate.isBefore(searchStart)) {
          currentDate.add(recurringGap, 'week');
        }
        break;

      case 'monthly':
        while (currentDate.isBefore(searchStart)) {
          currentDate.add(recurringGap, 'month');
          const lastDayOfMonth = currentDate.daysInMonth();
          if (dayOfMonth > lastDayOfMonth) {
            currentDate.date(lastDayOfMonth);
          } else {
            currentDate.date(dayOfMonth);
          }
        }
        break;
        
      case 'yearly':
        while (currentDate.isBefore(searchStart)) {
          currentDate.add(1, 'year');
          if (month === 1 && dayOfMonth === 29 && !moment([currentDate.year()]).isLeapYear()) {
            currentDate.month(month).date(28);
          } else {
            currentDate.month(month).date(dayOfMonth);
          }
        }
        break;
    }
  }

  // Generate occurrences within search range
  while (currentDate.isSameOrBefore(effectiveSearchEnd)) {
    if (frequency === 'weekly' && reservation.days_of_week && reservation.days_of_week.length > 0) {
      // Handle multiple days of the week
      const currentDayOfWeek = currentDate.isoWeekday();
      
      for (const targetDayOfWeek of reservation.days_of_week) {
        let daysToAdd = ((targetDayOfWeek - currentDayOfWeek) + 7) % 7;
        const eventDate = moment(currentDate).add(daysToAdd, 'days');
        
        if (eventDate.isSameOrBefore(effectiveSearchEnd) && eventDate.isSameOrAfter(searchStart)) {
          const eventStartDateTime = moment(eventDate).hour(originalStartDate.hour()).minute(originalStartDate.minute());
          const eventEndDateTime = moment(eventStartDateTime).add(eventDuration);
          
          occurrences.push({
            startDateTime: eventStartDateTime.clone(),
            endDateTime: eventEndDateTime.clone()
          });
        }
      }
    } else {
      // Handle single day occurrences
      const eventStartDateTime = moment(currentDate).hour(originalStartDate.hour()).minute(originalStartDate.minute());
      const eventEndDateTime = moment(eventStartDateTime).add(eventDuration);
      
      if (currentDate.isSameOrAfter(searchStart)) {
        occurrences.push({
          startDateTime: eventStartDateTime.clone(),
          endDateTime: eventEndDateTime.clone()
        });
      }
    }

    // Advance to next occurrence
    const dayOfMonth = originalStartDate.date();
    const lastDayOfMonth = currentDate.daysInMonth();
    switch (frequency) {
      case 'daily':
        currentDate.add(recurringGap, 'day');
        break;
        
      case 'weekly':
        currentDate.add(recurringGap, 'week');
        break;
        
      case 'monthly':
        currentDate.add(recurringGap, 'month');
        if (dayOfMonth > lastDayOfMonth) {
          currentDate.date(lastDayOfMonth);
        } else {
          currentDate.date(dayOfMonth);
        }
        break;
        
      case 'yearly':
        currentDate.add(1, 'year');
        break;
    }
  }

return occurrences;
};